/**
 * Work Queue Item
 * Individual issue row component for the work queue list
 */

import { forwardRef, memo } from 'react'
import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components'
import type { IssueSummary } from '../../../domain'
import { formatCategory } from '../../../utils/formatters'
import { TagBadges } from '../../../components'

const useStyles = makeStyles({
  item: {
    width: '100%',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandBackground}`,
    },
  },
  selected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    borderLeftColor: tokens.colorBrandBackground,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandBackground}`,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    overflow: 'hidden',
    width: '100%',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metadata: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
})

export interface WorkQueueItemProps {
  issue: IssueSummary
  isSelected: boolean
  tabIndex: number
  onSelect: (id: string) => void
}

const WorkQueueItemInner = forwardRef<HTMLDivElement, WorkQueueItemProps>(
  function WorkQueueItem({ issue, isSelected, tabIndex, onSelect }, ref) {
    const styles = useStyles()

    const handleClick = () => {
      onSelect(issue.id)
    }

    return (
      <div
        ref={ref}
        id={`work-queue-item-${issue.id}`}
        className={mergeClasses(styles.item, isSelected && styles.selected)}
        onClick={handleClick}
        role="option"
        aria-selected={isSelected}
        tabIndex={tabIndex}
      >
        <div className={styles.content}>
          <span className={styles.title}>{issue.title}</span>
          <span className={styles.metadata}>
            #{issue.id} • {issue.ageDisplay} • {formatCategory(issue.category)}
          </span>
          <TagBadges tags={issue.tags} className={styles.tags} />
        </div>
      </div>
    )
  }
)

/**
 * Memoized WorkQueueItem to prevent unnecessary re-renders.
 * Uses custom comparator - issue reference equality handles most cases.
 */
export const WorkQueueItem = memo(WorkQueueItemInner, (prevProps, nextProps) =>
  prevProps.issue === nextProps.issue &&
  prevProps.isSelected === nextProps.isSelected &&
  prevProps.tabIndex === nextProps.tabIndex &&
  prevProps.onSelect === nextProps.onSelect
)
